import React, { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { Upload, Download, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import { useLazyQuery } from '@apollo/client';
import { gql } from '@apollo/client';

const GET_ARREST_DATA_FOR_CHAMPING = gql`
  query GetArrestDataForChamping($champClientIds: [String]!) {
    getArrestDataForChamping(champClientIds: $champClientIds) {
      arrests {
        participant
        firstName
        lastName
        dob
        cid
        bookingnumber
        arrestdate
      }
      totalCount
    }
  }
`;

interface ArrestRecord {
  participant: string;
  firstName: string;
  lastName: string;
  dob: string;
  cid: string;
  bookingnumber: string;
  arrestdate: string;
}

interface ChampEnrollment {
  [key: string]: any;
}

interface MatchedRecord extends ChampEnrollment {
  matched: string;
  arrest_firstName: string;
  arrest_lastName: string;
  arrest_dob: string;
  arrest_cid: string;
  arrest_bookingNumber: string;
  arrest_date: string;
  days_to_arrest: number | string;
}

export const ChampArrestMatcher: React.FC = () => {
  const [champFile, setChampFile] = useState<File | null>(null);
  const [champData, setChampData] = useState<ChampEnrollment[]>([]);
  const [arrestData, setArrestData] = useState<ArrestRecord[]>([]);
  const [matchedData, setMatchedData] = useState<MatchedRecord[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    matched: 0,
    unmatched: 0,
    avgDays: 0
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [getArrestData, { loading: arrestLoading }] = useLazyQuery(GET_ARREST_DATA_FOR_CHAMPING, {
    onCompleted: (data) => {
      setArrestData(data.getArrestDataForChamping.arrests);
      setSuccess(`Arrest data loaded: ${data.getArrestDataForChamping.totalCount} records`);
    },
    onError: (error) => {
      setError('Error loading arrest data: ' + error.message);
    }
  });

  // Auto-load arrest data when CHAMP data is loaded
  const loadArrestDataForChamp = useCallback((champClientIds: string[]) => {
    if (champClientIds.length === 0) return;
    
    setError(null);
    getArrestData({
      variables: { champClientIds }
    });
  }, [getArrestData]);

  const parseCSV = (text: string): any[] => {
    const lines = text.split('\n');
    const headers = lines[0].split(',').map(h => h.trim());
    const data = [];
    
    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue;
      const values = lines[i].split(',');
      const row: any = {};
      headers.forEach((header, index) => {
        row[header] = values[index]?.trim() || '';
      });
      data.push(row);
    }
    return data;
  };

  const handleChampFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setChampFile(file);
    setError(null);
    
    const reader = new FileReader();
    
    if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
      reader.onload = (evt) => {
        try {
          const data = new Uint8Array(evt.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array', cellDates: true });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet, { raw: false, dateNF: 'yyyy-mm-dd' }) as ChampEnrollment[];
          setChampData(jsonData);
          
          // Extract ClientIDs and load arrest data automatically
          const clientIds = jsonData
            .map(row => row['ClientID']?.toString().trim())
            .filter(id => id);
          
          setSuccess(`CHAMP file loaded: ${jsonData.length} records. Loading arrest data...`);
          loadArrestDataForChamp(clientIds);
        } catch (err) {
          setError('Error reading CHAMP file');
        }
      };
      reader.readAsArrayBuffer(file);
    } else if (file.name.endsWith('.csv')) {
      reader.onload = (evt) => {
        try {
          const text = evt.target?.result as string;
          const jsonData = parseCSV(text) as ChampEnrollment[];
          setChampData(jsonData);
          
          // Extract ClientIDs and load arrest data automatically
          const clientIds = jsonData
            .map(row => row['ClientID']?.toString().trim())
            .filter(id => id);
          
          setSuccess(`CHAMP file loaded: ${jsonData.length} records. Loading arrest data...`);
          loadArrestDataForChamp(clientIds);
        } catch (err) {
          setError('Error reading CHAMP file');
        }
      };
      reader.readAsText(file);
    }
  }, [loadArrestDataForChamp]);

  const normalizeDate = (dateValue: any): Date | null => {
    if (!dateValue) return null;
    
    if (dateValue instanceof Date) return dateValue;
    
    const dateStr = String(dateValue).trim();
    
    // Handle Excel serial date
    if (dateStr.match(/^\d{5}$/)) {
      const excelDate = parseInt(dateStr);
      return new Date((excelDate - 25569) * 86400 * 1000);
    }
    
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? null : date;
  };

  const findClosestArrestAfterEnrollment = (
    participantId: string | null, 
    enrollmentDate: any, 
    arrests: ArrestRecord[]
  ) => {
    if (!participantId) return null;
    const enrollDate = normalizeDate(enrollmentDate);
    if (!enrollDate) return null;
    
    let closestArrest: (ArrestRecord & { daysAfterEnrollment: number }) | null = null;
    let minDaysDiff = Infinity;
    
    for (const arrest of arrests) {
      const arrestDate = normalizeDate(arrest.arrestdate);
      if (!arrestDate) continue;
      
      const daysDiff = Math.floor((arrestDate.getTime() - enrollDate.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysDiff > 0 && daysDiff < minDaysDiff) {
        minDaysDiff = daysDiff;
        closestArrest = { ...arrest, daysAfterEnrollment: daysDiff };
      }
    }
    
    return closestArrest;
  };

  const processData = useCallback(() => {
    if (!champData.length) {
      setError('Please upload CHAMP file first');
      return;
    }
    
    if (!arrestData.length) {
      setError('Arrest data not loaded yet. Please wait for data to load.');
      return;
    }
    
    setIsProcessing(true);
    setError(null);
    setSuccess(null);
    
    setTimeout(() => {
      try {
        // Group arrests by participant ID
        const arrestsByParticipant: { [key: string]: ArrestRecord[] } = {};
        arrestData.forEach(arrest => {
          const id = arrest.participant;
          if (!arrestsByParticipant[id]) {
            arrestsByParticipant[id] = [];
          }
          arrestsByParticipant[id].push(arrest);
        });
        
        // Process CHAMP data
        const matched: MatchedRecord[] = [];
        let matchCount = 0;
        let totalDays = 0;
        let daysCount = 0;
        
        // Find enrollment date and participant ID fields
        const enrollmentDateFields = ['Enrollment Date', 'enrollment_date', 'EnrollmentDate', 'Date', 'date'];
        const participantIdFields = ['Participant ID', 'participant_id', 'ParticipantID', 'participant', 'ID', 'id'];
        
        champData.forEach(enrollment => {
          let enrollmentDate = null;
          let participantId = null;
          
          // Find enrollment date field
          for (const field of enrollmentDateFields) {
            if (enrollment[field]) {
              enrollmentDate = enrollment[field];
              break;
            }
          }
          
          // Find participant ID field
          for (const field of participantIdFields) {
            if (enrollment[field]) {
              participantId = String(enrollment[field]).trim();
              break;
            }
          }
          
          const arrests = participantId ? (arrestsByParticipant[participantId] || []) : [];
          const closestArrest = findClosestArrestAfterEnrollment(participantId, enrollmentDate, arrests);
          
          const matchedRecord: MatchedRecord = {
            ...enrollment,
            matched: closestArrest ? 'Yes' : 'No',
            arrest_firstName: closestArrest?.firstName || '',
            arrest_lastName: closestArrest?.lastName || '',
            arrest_dob: closestArrest?.dob || '',
            arrest_cid: closestArrest?.cid || '',
            arrest_bookingNumber: closestArrest?.bookingnumber || '',
            arrest_date: closestArrest?.arrestdate || '',
            days_to_arrest: closestArrest?.daysAfterEnrollment || ''
          };
          
          matched.push(matchedRecord);
          
          if (closestArrest) {
            matchCount++;
            if (closestArrest.daysAfterEnrollment) {
              totalDays += closestArrest.daysAfterEnrollment;
              daysCount++;
            }
          }
        });
        
        setMatchedData(matched);
        setStats({
          total: champData.length,
          matched: matchCount,
          unmatched: champData.length - matchCount,
          avgDays: daysCount > 0 ? Math.round(totalDays / daysCount) : 0
        });
        
        setSuccess(`Processing complete! Found ${matchCount} matches out of ${champData.length} enrollments.`);
        
      } catch (err) {
        setError('Error processing data: ' + (err as Error).message);
      } finally {
        setIsProcessing(false);
      }
    }, 100);
  }, [champData, arrestData]);

  const exportResults = useCallback(() => {
    if (!matchedData.length) return;
    
    const ws = XLSX.utils.json_to_sheet(matchedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Matched Data');
    
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, 'champ_arrest_matched_data.xlsx');
    
    setSuccess('Data exported successfully!');
  }, [matchedData]);

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded border border-gray-200">
          <div className="border-b border-gray-200 p-4">
            <h1 className="text-xl font-semibold text-gray-900">CHAMP Arrest Data Matcher</h1>
            <p className="mt-1 text-sm text-gray-600">Match arrest records to CHAMP enrollments based on closest arrest date after enrollment</p>
          </div>
          
          <div className="p-6">
            {/* File Upload Section */}
            <div className="grid grid-cols-1 gap-6 mb-6">
              <div className="border border-dashed border-gray-300 rounded p-4 text-center max-w-md mx-auto">
                <FileText className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <h3 className="text-sm font-medium mb-2">CHAMP Enrollments File</h3>
                <p className="text-xs text-gray-600 mb-3">Upload CHAMP enrollment data. Arrest data will be loaded automatically.</p>
                <input
                  type="file"
                  id="champFile"
                  className="hidden"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleChampFileUpload}
                />
                <label
                  htmlFor="champFile"
                  className="inline-flex items-center px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 cursor-pointer"
                >
                  <Upload className="w-3 h-3 mr-1" />
                  Choose File
                </label>
                {champFile && (
                  <div className="mt-3 text-sm text-gray-600">
                    Selected: {champFile.name}
                  </div>
                )}
                {arrestLoading && (
                  <div className="mt-3 text-sm text-blue-600">
                    Loading arrest data from database...
                  </div>
                )}
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex justify-center gap-3 mb-6">
              <button
                onClick={processData}
                disabled={!champData.length || !arrestData.length || isProcessing || arrestLoading}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {arrestLoading ? 'Loading Arrest Data...' : isProcessing ? 'Processing...' : 'Process & Match Data'}
              </button>
              
              {matchedData.length > 0 && (
                <button
                  onClick={exportResults}
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                >
                  <Download className="w-3 h-3 mr-1" />
                  Export Results
                </button>
              )}
            </div>
            
            {/* Messages */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded flex items-center">
                <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
                <span className="text-red-800 text-sm">{error}</span>
              </div>
            )}
            
            {success && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded flex items-center">
                <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                <span className="text-green-800 text-sm">{success}</span>
              </div>
            )}
            
            {/* Statistics */}
            {stats.total > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
                <div className="bg-gray-50 border border-gray-200 rounded p-3">
                  <div className="text-lg font-semibold text-gray-900">{stats.total}</div>
                  <div className="text-xs text-gray-600">Total Enrollments</div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded p-3">
                  <div className="text-lg font-semibold text-green-900">{stats.matched}</div>
                  <div className="text-xs text-green-700">Matches Found</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded p-3">
                  <div className="text-lg font-semibold text-red-900">{stats.unmatched}</div>
                  <div className="text-xs text-red-700">No Matches</div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                  <div className="text-lg font-semibold text-blue-900">{stats.avgDays || 'N/A'}</div>
                  <div className="text-xs text-blue-700">Avg Days to Arrest</div>
                </div>
              </div>
            )}
            
            {/* Results Table */}
            {matchedData.length > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {Object.keys(matchedData[0]).map(key => (
                        <th
                          key={key}
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          {key.replace(/_/g, ' ')}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {matchedData.slice(0, 100).map((row, idx) => (
                      <tr key={idx} className={row.matched === 'Yes' ? 'bg-green-50' : 'bg-red-50'}>
                        {Object.values(row).map((val, i) => (
                          <td key={i} className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                            {String(val)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {matchedData.length > 100 && (
                  <div className="text-center py-4 text-gray-600">
                    Showing first 100 of {matchedData.length} records. Export to see all.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};